import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect } from "react";
import profaImage from "../assets/profa-1.webp";
import backgroundVideo from "../assets/BackVideo.mp4";

const Hero = () => {
  const textVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  useEffect(() => {
    // Force the browser to prioritize the LCP element
    const lcpElement = document.getElementById('lcp-element');
    if (lcpElement) {
      // Apply any necessary optimizations without changing the styling
      lcpElement.style.visibility = 'visible';
      
      // Use the browser's Largest Contentful Paint API if available
      if ('LargestContentfulPaint' in window) {
        // Signal to the browser that this is an important element
        new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          if (entries.length > 0) {
            console.log('LCP element identified:', entries[0]);
          }
        }).observe({ type: 'largest-contentful-paint', buffered: true });
      }
    }
  }, []);

  const letterVariants = {
    hidden: { 
      opacity: 0,
      y: 20,
      scale: 0.9,
      rotate: -5
    },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.3,
        delay: i * 0.08,
        type: "spring",
        stiffness: 100,
        damping: 10
      },
    }),
    hover: {
      scale: 1.1,
      color: "rgb(104, 60, 171)",
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03,
        delayChildren: 0.2
      }
    }
  };

  const nameText = "Miloš Živković".split("");
  const profaText = "Profa".split("");

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 sm:pt-24">
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
        <div className="absolute inset-0 bg-black/50 z-10" />{" "}
        {/* Overlay to make content more readable */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          // eslint-disable-next-line react/no-unknown-property
          fetchpriority="high"
          className="absolute w-full h-full object-cover scale-105 transform motion-safe:animate-subtle-zoom"
          style={{
            animation: "subtle-zoom 20s infinite alternate",
          }}
        >
          <source src={backgroundVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>

      {/* Content */}
      <div className="relative z-20 w-full py-8 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">
            {/* Image Section */}
            <motion.div 
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative flex justify-center items-center order-1 lg:order-none"
            >
              <motion.div 
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="w-[280px] h-[280px] sm:w-[350px] sm:h-[350px] lg:w-[450px] lg:h-[450px] relative motion-safe:animate-float"
              >
                <img
                  src={profaImage}
                  alt="Miloš Živković Profa - Astrolog i Hipnoterapeut"
                  width="450"
                  height="450"
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-contain transform hover:scale-105 transition-transform duration-500"
                  style={{
                    filter: "drop-shadow(0px 8px 30px rgba(104, 60, 171, 0.3))",
                  }}
                />
              </motion.div>
            </motion.div>

            {/* Text Content Section */}
            <motion.div 
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-center lg:text-left space-y-4 sm:space-y-6 md:space-y-8 motion-safe:animate-fade-in"
            >
              <motion.h1 
                className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight hero-heading"
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                <motion.span 
                  className="block text-white bg-clip-text font-['Lora'] flex flex-wrap justify-center lg:justify-start"
                  variants={containerVariants}
                >
                  {nameText.map((char, index) => (
                    <motion.span
                      key={index}
                      custom={index}
                      variants={letterVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover="hover"
                      className="inline-block cursor-default"
                      style={{ textShadow: "0 0 30px rgba(104, 60, 171, 0.3)" }}
                    >
                      {char === " " ? "\u00A0" : char}
                    </motion.span>
                  ))}
                </motion.span>
                <motion.span 
                  className="block text-white bg-clip-text font-['Lora'] flex flex-wrap justify-center lg:justify-start mt-2"
                  variants={containerVariants}
                >
                  {profaText.map((char, index) => (
                    <motion.span
                      key={index}
                      custom={index}
                      variants={letterVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover="hover"
                      className="inline-block cursor-default"
                      style={{ textShadow: "0 0 30px rgba(104, 60, 171, 0.3)" }}
                    >
                      {char}
                    </motion.span>
                  ))}
                </motion.span>
              </motion.h1>
              <motion.span 
                className="block text-[rgb(49,135,208)] mt-2 sm:mt-3 bg-clip-text text-2xl sm:text-3xl lg:text-5xl font-['Lora'] font-extrabold"
                variants={textVariants}
              >
                Transformacija Života
              </motion.span>
              {/* Optimized LCP element - using regular HTML instead of motion for critical content */}
              <p 
                className="text-base sm:text-lg lg:text-xl text-gray-100 font-medium max-w-2xl mx-auto lg:mx-0 leading-relaxed"
                style={{ fontDisplay: 'swap', visibility: 'visible', contain: 'content', contentVisibility: 'auto' }}
                id="lcp-element"
                data-lcp="true"
                data-priority="high"
              >
                Započnite svoje putovanje ka ličnom i duhovnom 
                razvoju uz profesionalnu podršku i dokazane metode
              </p>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.4 }}
                className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center lg:justify-start"
              >
                <Link
                  to="/hipnoze/"
                  className="rounded-xl bg-[rgb(104,60,171)] px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white shadow-lg hover:bg-[rgb(49,135,208)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[rgb(104,60,171)] focus:ring-offset-2"
                >
                  Hipnoze
                </Link>
                <Link
                  to="/astroloske-konsultacije/"
                  className="rounded-xl bg-white/10 backdrop-blur-sm px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white border border-white/20 shadow-lg hover:bg-white/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
                >
                  Astrološke konsultacije
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
